import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Trash2, Edit, ExternalLink, AlertTriangle, Info,
  LayoutGrid, GraduationCap, FileText, Boxes, CalendarDays,
  Globe, BarChart, Table, Database, Code, Settings, Wrench,
  Zap, Rocket, Target, User, Briefcase, Shield, Heart,
} from 'lucide-react';

// Icons available for custom profile tabs
const PROFILE_TAB_ICONS = {
  ExternalLink,
  Globe,
  BarChart,
  Table,
  Database,
  Code,
  Settings,
  Wrench,
  Zap,
  Rocket,
  Target,
  User,
  Briefcase,
  Shield,
  Heart,
  FileText,
  LayoutGrid,
};

const AVAILABLE_ICONS = Object.keys(PROFILE_TAB_ICONS);

// Colors for tabs
const TAB_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
  '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#ec4899', '#f43f5e'
];

// Available variables that can be inserted into URLs
const AVAILABLE_VARS = [
  { key: 'user_profile.id', label: 'User Profile ID', description: 'Unique profile identifier' },
  { key: 'user_profile.email', label: 'Email', description: 'User email address' },
  { key: 'user_profile.first_name', label: 'First Name', description: 'User first name' },
  { key: 'user_profile.last_name', label: 'Last Name', description: 'User last name' },
  { key: 'user_profile.display_name', label: 'Display Name', description: 'User display name' },
  { key: 'user_profile.phone_number', label: 'Phone Number', description: 'User phone number' },
  { key: 'user_profile.job_title', label: 'Job Title', description: 'User job title' },
  { key: 'user_profile.department', label: 'Department', description: 'User department' },
  { key: 'user_profile.employee_id', label: 'Employee ID', description: 'Employee ID field' },
  { key: 'company.id', label: 'Company ID', description: 'Company identifier' },
  { key: 'company.name', label: 'Company Name', description: 'Company name' },
];

function getTabIcon(iconName) {
  return PROFILE_TAB_ICONS[iconName] || ExternalLink;
}

/**
 * CustomProfileTabsManager - Admin UI for configuring custom embedded tabs on user profiles
 *
 * @param {Array} customTabs - Array of custom tab objects
 * @param {Function} onChange - Callback when tabs are modified (receives updated array)
 */
export function CustomProfileTabsManager({ customTabs = [], onChange }) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingTab, setEditingTab] = useState(null);
  const [deletingTab, setDeletingTab] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    icon: 'ExternalLink',
    color: '#6366f1'
  });
  const [urlError, setUrlError] = useState('');
  const [urlWarning, setUrlWarning] = useState('');

  // Validate URL
  const validateUrl = useCallback((url) => {
    // Allow URLs with variable placeholders by stripping them for validation
    const testUrl = url.replace(/\{[^}]+\}/g, 'placeholder');
    if (!testUrl.trim()) {
      return { error: 'URL is required', warning: '' };
    }
    try {
      const parsed = new URL(testUrl);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        return { error: 'URL must start with http:// or https://', warning: '' };
      }
      if (parsed.protocol === 'http:' && !parsed.hostname.includes('localhost') && !parsed.hostname.includes('127.0.0.1')) {
        return { error: '', warning: 'HTTP connections are not secure. Consider using HTTPS if available.' };
      }
      return { error: '', warning: '' };
    } catch {
      return { error: 'Please enter a valid URL (variables like {user_profile.id} are allowed)', warning: '' };
    }
  }, []);

  // Generate UUID
  const generateUUID = useCallback(() => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }, []);

  // Insert variable at cursor position in URL field
  const insertVariable = useCallback((varKey) => {
    const urlInput = document.getElementById('tab-url');
    const cursorPos = urlInput?.selectionStart || formData.url.length;
    const before = formData.url.slice(0, cursorPos);
    const after = formData.url.slice(cursorPos);
    const newUrl = `${before}{${varKey}}${after}`;
    setFormData(prev => ({ ...prev, url: newUrl }));
    setUrlError('');
    // Validate new URL
    if (newUrl.trim()) {
      const { warning } = validateUrl(newUrl);
      setUrlWarning(warning);
    }
  }, [formData.url, validateUrl]);

  // Save tab
  const handleSaveTab = useCallback(() => {
    const { error } = validateUrl(formData.url);
    if (error) {
      setUrlError(error);
      return;
    }

    if (!formData.name.trim()) return;

    const newTab = {
      id: editingTab?.id || generateUUID(),
      name: formData.name.trim(),
      url: formData.url.trim(),
      icon: formData.icon,
      color: formData.color,
      enabled: editingTab?.enabled ?? true,
      created_date: editingTab?.created_date || new Date().toISOString()
    };

    let updatedTabs;
    if (editingTab) {
      updatedTabs = customTabs.map(t => t.id === editingTab.id ? newTab : t);
    } else {
      updatedTabs = [...customTabs, newTab];
    }

    onChange(updatedTabs);
    // Close dialog
    setIsAddDialogOpen(false);
    setEditingTab(null);
    setFormData({ name: '', url: '', icon: 'ExternalLink', color: '#6366f1' });
    setUrlError('');
    setUrlWarning('');
  }, [formData, editingTab, customTabs, onChange, validateUrl, generateUUID]);

  // Delete tab
  const handleDeleteTab = useCallback(() => {
    if (!deletingTab) return;
    onChange(customTabs.filter(t => t.id !== deletingTab.id));
    setDeletingTab(null);
  }, [deletingTab, customTabs, onChange]);

  // Toggle enabled
  const handleToggleEnabled = useCallback((tabId) => {
    onChange(customTabs.map(t =>
      t.id === tabId ? { ...t, enabled: !t.enabled } : t
    ));
  }, [customTabs, onChange]);

  // Close dialog and reset
  const handleCloseDialog = useCallback(() => {
    setIsAddDialogOpen(false);
    setEditingTab(null);
    setFormData({ name: '', url: '', icon: 'ExternalLink', color: '#6366f1' });
    setUrlError('');
    setUrlWarning('');
  }, []);

  // Open edit dialog
  const handleEditTab = useCallback((tab) => {
    setEditingTab(tab);
    setFormData({
      name: tab.name,
      url: tab.url,
      icon: tab.icon || 'ExternalLink',
      color: tab.color || '#6366f1'
    });
    setIsAddDialogOpen(true);
  }, []);

  // Open add dialog
  const handleAddTab = useCallback(() => {
    setEditingTab(null);
    setFormData({ name: '', url: '', icon: 'ExternalLink', color: '#6366f1' });
    setUrlError('');
    setIsAddDialogOpen(true);
  }, []);

  // Extract variables from URL for preview
  const extractVars = (url) => {
    const matches = url.match(/\{([^}]+)\}/g);
    return matches ? matches.map(m => m.slice(1, -1)) : [];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Custom Profile Tabs</h3>
          <p className="text-sm text-muted-foreground">
            Add embedded tabs to user profiles across your company. Use variables to pass user-specific data.
          </p>
        </div>
        <Button size="sm" onClick={handleAddTab}>
          <Plus className="h-4 w-4 mr-1.5" />
          Add Tab
        </Button>
      </div>

      {customTabs.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-6 text-center">
          <ExternalLink className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            No custom profile tabs yet. Embed external views, dashboards, or tools directly in user profiles.
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={handleAddTab}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add Your First Tab
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {customTabs.map(tab => {
            const IconComponent = getTabIcon(tab.icon);
            const vars = extractVars(tab.url);
            return (
              <div
                key={tab.id}
                className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                  tab.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'
                }`}
              >
                <div
                  className="p-2 rounded-lg flex-shrink-0"
                  style={{ backgroundColor: (tab.color || '#6366f1') + '20' }}
                >
                  <IconComponent className="h-4 w-4" style={{ color: tab.color || '#6366f1' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{tab.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{tab.url}</p>
                  {vars.length > 0 && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {vars.map(v => (
                        <Badge key={v} variant="secondary" className="text-[10px] py-0 px-1.5">
                          {v}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <Switch
                  checked={tab.enabled}
                  onCheckedChange={() => handleToggleEnabled(tab.id)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => handleEditTab(tab)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setDeletingTab(tab)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTab ? 'Edit Profile Tab' : 'Add Profile Tab'}
            </DialogTitle>
            <DialogDescription>
              {editingTab
                ? 'Update the details for this embedded profile tab.'
                : 'Add an embedded tab that will appear on all user profiles.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tab-name">Tab Name</Label>
              <Input
                id="tab-name"
                placeholder="e.g., Employee Records"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tab-url">URL</Label>
              <Input
                id="tab-url"
                placeholder="https://app.example.com/view?id={user_profile.id}"
                value={formData.url}
                onChange={(e) => {
                  const newUrl = e.target.value;
                  setFormData(prev => ({ ...prev, url: newUrl }));
                  setUrlError('');
                  if (newUrl.trim()) {
                    const { warning } = validateUrl(newUrl);
                    setUrlWarning(warning);
                  } else {
                    setUrlWarning('');
                  }
                }}
                className={urlError ? 'border-red-500' : urlWarning ? 'border-amber-500' : ''}
              />
              {urlError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {urlError}
                </p>
              )}
              {!urlError && urlWarning && (
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {urlWarning}
                </p>
              )}
            </div>

            {/* Variable Insertion */}
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Insert Variable</Label>
                <Info className="h-3 w-3 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground -mt-1">
                Click a variable to insert it into the URL. Variables are replaced with real data for each user.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {AVAILABLE_VARS.map(v => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVariable(v.key)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300 transition-colors cursor-pointer"
                    title={v.description}
                  >
                    <code className="text-[10px] text-violet-600">{`{${v.key}}`}</code>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={formData.icon}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, icon: v }))}
                >
                  <SelectTrigger>
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const Icon = getTabIcon(formData.icon);
                          return <Icon className="h-4 w-4" />;
                        })()}
                        <span>{formData.icon}</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_ICONS.map(iconName => {
                      const Icon = PROFILE_TAB_ICONS[iconName];
                      return (
                        <SelectItem key={iconName} value={iconName}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span>{iconName}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-1.5 p-2 border rounded-md">
                  {TAB_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                        formData.color === color
                          ? 'border-gray-900 scale-110'
                          : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* URL Preview */}
            {formData.url && extractVars(formData.url).length > 0 && (
              <div className="bg-gray-50 border rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-medium text-gray-600">URL Preview (example)</p>
                <p className="text-xs font-mono text-gray-800 break-all">
                  {formData.url
                    .replace('{user_profile.id}', 'abc-123-def')
                    .replace('{user_profile.email}', 'john@example.com')
                    .replace('{user_profile.first_name}', 'John')
                    .replace('{user_profile.last_name}', 'Smith')
                    .replace('{user_profile.display_name}', 'John Smith')
                    .replace('{user_profile.phone_number}', '+44123456789')
                    .replace('{user_profile.job_title}', 'Engineer')
                    .replace('{user_profile.department}', 'Operations')
                    .replace('{user_profile.employee_id}', 'EMP-001')
                    .replace('{company.id}', 'comp-456')
                    .replace('{company.name}', 'Acme Corp')
                  }
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveTab}
              disabled={!formData.name.trim() || !formData.url.trim()}
            >
              {editingTab ? 'Save Changes' : 'Add Tab'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingTab} onOpenChange={() => setDeletingTab(null)}>
        <AlertDialogContent className="max-w-md p-0 overflow-hidden rounded-xl border-slate-200">
          <div className="px-6 py-5 bg-red-50">
            <h3 className="text-lg font-semibold text-slate-800">Delete Profile Tab?</h3>
            <p className="text-sm text-slate-600 mt-1">This will remove the tab from all user profiles.</p>
          </div>
          {deletingTab && (
            <div className="px-6 py-4">
              <div className="flex items-center gap-3">
                <div
                  className="p-2 rounded-lg flex-shrink-0"
                  style={{ backgroundColor: (deletingTab.color || '#6366f1') + '20' }}
                >
                  {(() => {
                    const Icon = getTabIcon(deletingTab.icon);
                    return <Icon className="h-4 w-4" style={{ color: deletingTab.color || '#6366f1' }} />;
                  })()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{deletingTab.name}</p>
                  <p className="text-xs text-slate-500 truncate">{deletingTab.url}</p>
                </div>
              </div>
            </div>
          )}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
            <AlertDialogCancel className="border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTab}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
